import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import Select from 'react-select';

const selectStyles = {
  indicatorSeparator: () => ({ display: 'none' }),
  control: (base, state) => ({
    ...base,
    backgroundColor: '#020617', // slate-950
    borderColor: state.isFocused ? '#ffffff80' : '#334155', // slate-700
    color: 'white',
    boxShadow: state.isFocused ? '0 0 0 1px #ffffff33' : 'none',
    minHeight: '38px',
    height: '38px',
    borderRadius: '0.5rem',
    cursor: 'text',
    '&:hover': { borderColor: '#ffffff80' },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 12px' }),
  input: (base) => ({ ...base, color: 'white', margin: 0, padding: 0, caretColor: 'auto' }),
  singleValue: (base) => ({ ...base, color: 'white', fontSize: '0.75rem' }),
  placeholder: (base) => ({ ...base, color: '#475569', fontSize: '0.75rem' }),
  menu: (base) => ({
    ...base,
    backgroundColor: '#0f172a', // slate-900
    border: '1px solid #334155',
    zIndex: 9999,
    borderRadius: '0.5rem',
    overflow: 'hidden'
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? '#1e293b' : 'transparent', // slate-800
    color: '#e2e8f0',
    fontSize: '0.75rem',
    cursor: 'pointer',
    '&:active': { backgroundColor: '#334155' },
  }),
};

const MarginPercentageMaster = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/hospital-reports`, { withCredentials: true });
      const formatted = res.data.map(r => ({
        ...r,
        margin_percentage: r.margin_percentage !== null && r.margin_percentage !== undefined ? String(r.margin_percentage).replace(/\.00$/, '') : '25'
      }));
      setReports(formatted);
      if (formatted.length > 0 && !selectedReportId) {
        setSelectedReportId(formatted[0].id);
      }
    } catch (err) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (index, value) => {
    const updated = [...reports];
    updated[index].margin_percentage = value;
    setReports(updated);
  };

  const handleSave = async (index) => {
    const report = reports[index];
    setSavingId(report.id);
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/hospital-reports/${report.id}/margin`, {
        margin_percentage: report.margin_percentage || 25
      }, { withCredentials: true });
      toast.success('Margin percentage updated successfully!');
    } catch (err) {
      toast.error('Failed to update margin percentage');
    } finally {
      setSavingId(null);
    }
  };

  const activeReport = reports.find(r => r.id === selectedReportId);
  const activeReportIndex = reports.findIndex(r => r.id === selectedReportId);

  const filteredReports = reports.filter(r => 
    (r.report_name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout>
      <div className="min-h-screen bg-[#020617] p-6 lg:p-10 font-sans text-slate-200">
        <div className="max-w-6xl mx-auto space-y-6">
          
          {/* Header Section */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
              </div>
              <div>
                <h2 className="font-black text-white text-lg tracking-tight">SETTING PERCENTAGE</h2>
                <p className="text-xs text-indigo-400/70 font-medium">Configure global Margin Percentage for each project</p>
              </div>
            </div>
          </div>

          {/* 2-COLUMN DRILL DOWN UI */}
          <div className="flex gap-4 min-h-[500px] h-[calc(100vh-180px)] w-full">
            
            {/* PANE 1: Projects List */}
            <div className="w-[320px] flex flex-col bg-slate-900/50 border border-slate-700/50 rounded-2xl overflow-hidden shadow-inner shrink-0 h-fit max-h-full">
              <div className="p-4 bg-slate-800/80 border-b border-slate-700 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-white font-bold text-sm uppercase tracking-wider">PROJECTS</h3>
                  <p className="text-xs text-slate-400">{filteredReports.length} items</p>
                </div>
                <div className="relative z-50">
                  <Select
                    options={reports.map(r => ({ value: r.id, label: r.report_name }))}
                    value={selectedReportId ? { value: selectedReportId, label: reports.find(r => r.id === selectedReportId)?.report_name || '' } : null}
                    onChange={selected => {
                      if (selected) {
                        setSelectedReportId(selected.value);
                        setSearchTerm(selected.label);
                      } else {
                        setSearchTerm('');
                      }
                    }}
                    onInputChange={(val) => {
                      // Only update searchTerm on user input, not when selected changes
                      if (val) setSearchTerm(val);
                      else setSearchTerm('');
                    }}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    placeholder="Search projects..."
                    isSearchable
                    isClearable
                  />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-3 custom-scrollbar space-y-2">
                {loading ? (
                  <div className="text-center py-10 text-slate-500 text-xs italic flex flex-col items-center">
                    <svg className="w-6 h-6 animate-spin mb-3 text-indigo-500" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                    Loading projects...
                  </div>
                ) : filteredReports.length === 0 ? (
                  <div className="text-center py-10 text-slate-500 text-xs italic">No projects found.</div>
                ) : (
                  filteredReports.map((report, idx) => {
                    const isSelected = selectedReportId === report.id;
                    return (
                      <div 
                        key={report.id} 
                        onClick={() => setSelectedReportId(report.id)} 
                        className={`cursor-pointer p-3 rounded-xl border transition-all select-none group flex flex-col gap-2 shadow-sm ${isSelected ? 'bg-indigo-500/10 border-indigo-500 shadow-indigo-500/20' : 'bg-slate-800 border-slate-700 hover:border-slate-500 hover:bg-slate-700/80'}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-3 overflow-hidden">
                            <div className={`w-6 h-6 shrink-0 rounded-md flex items-center justify-center font-black text-[10px] ${isSelected ? 'bg-indigo-500 text-white' : 'bg-slate-700 text-slate-400'}`}>
                              {idx + 1}
                            </div>
                            <h4 className={`font-bold text-sm leading-tight ${isSelected ? 'text-indigo-300' : 'text-slate-200'}`}>
                              {report.report_name}
                            </h4>
                          </div>
                          <Link 
                            to={`/hospital-report?project_id=${report.id}`}
                            className={`shrink-0 p-1 rounded-md transition-colors ${isSelected ? 'text-indigo-400 hover:bg-indigo-500/20' : 'text-slate-500 hover:text-slate-300 hover:bg-slate-700'} opacity-0 group-hover:opacity-100`}
                            title="Open Project"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                          </Link>
                        </div>
                        <div className={`text-xs font-mono font-bold text-right ${isSelected ? 'text-indigo-300' : 'text-slate-400'}`}>
                          Margin: {report.margin_percentage}%
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* PANE 2: Configuration Editor */}
            <div className="flex-1 flex flex-col bg-slate-900/50 border border-slate-700/50 rounded-2xl overflow-hidden shadow-inner h-fit max-h-full">
              {!activeReport ? (
                <div className="flex-1 flex flex-col items-center justify-center min-h-[400px]">
                  <svg className="w-16 h-16 text-slate-700 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  <p className="text-slate-400 text-sm">Select a project to configure its margin percentage</p>
                </div>
              ) : (
                <>
                  <div className="p-4 bg-slate-800/80 border-b border-slate-700 flex items-center justify-between">
                    <div>
                      <h3 className="text-white font-bold text-sm uppercase tracking-wider">Configuration Settings</h3>
                      <p className="text-xs text-slate-400 mt-1">{activeReport.report_name}</p>
                    </div>
                  </div>
                  
                  <div className="flex-1 p-8 md:p-12 flex flex-col items-center justify-center relative">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-indigo-500/5 rounded-full blur-3xl"></div>
                    
                    <div className="relative z-10 bg-slate-800/80 border border-slate-700 p-8 rounded-3xl shadow-2xl w-full max-w-sm text-center">
                      <label className="block text-sm font-bold text-slate-400 uppercase tracking-widest mb-6">Global Margin Percentage</label>
                      
                      <div className="flex items-center justify-center gap-3 mb-8">
                        <input
                          type="number"
                          className="w-32 bg-slate-950 border border-slate-600 rounded-xl px-4 py-3 text-4xl text-center font-black font-mono text-indigo-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50 transition-all shadow-inner"
                          value={activeReport.margin_percentage}
                          onChange={(e) => handleChange(activeReportIndex, e.target.value)}
                        />
                        <span className="text-3xl font-black text-slate-500 select-none">%</span>
                      </div>
                      
                      <button
                        onClick={() => handleSave(activeReportIndex)}
                        disabled={savingId === activeReport.id}
                        className="w-full bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white font-bold py-4 px-6 rounded-xl transition-all shadow-lg shadow-indigo-500/25 disabled:opacity-50 flex items-center justify-center gap-3"
                      >
                        {savingId === activeReport.id ? (
                          <><svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Saving...</>
                        ) : (
                          <><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>Save Changes</>
                        )}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

        </div>
      </div>
    </Layout>
  );
};

export default MarginPercentageMaster;
